import { forwardRef } from "react";
import { formatDate } from "../../data/format";
import {
  RUTA_MAGICA_CONTACTS,
  SCHOOL_VISIT_CONDITIONS,
  SCHOOL_VISIT_INCLUDES,
  SCHOOL_VISIT_ITINERARY,
  SCHOOL_VISIT_NOTES,
  describeSchoolDetails,
  listJoin,
  paymentSchedule,
  schoolMenus,
} from "@rutamagica/contracts";
import type { CommerceQuote } from "../../data/workspace";
import { money, serviceNames } from "./shared";


const icons: Record<string, string> = { barro: "pot", amaranto: "grain", cacao: "cacao", pintura: "paint", collares: "beads", marketing: "screen", numeros: "book" };
const clock = (value?: string) => {
  const match = /^(\d{2}):(\d{2})/.exec(value ?? "");
  if (!match) return "Por definir";
  const hours = Number(match[1]);
  return `${hours % 12 || 12}:${match[2]} ${hours < 12 ? "a.m." : "p.m."}`;
};

/** The quote as the customer reads it, in the style of the school brochures (same layout as the PDF). */
export const QuoteDocument = forwardRef<HTMLElement, { quote: CommerceQuote; showMoney: boolean }>(
  function QuoteDocument({ quote, showMoney }, ref) {
    const items = quote.items.length
      ? quote.items
      : [{ description: serviceNames[quote.service] ?? "Servicio", quantity: quote.people, unit_price: quote.people ? quote.total / quote.people : quote.total, total: quote.total }];
    const school = quote.service === "school" ? describeSchoolDetails(quote.school) : [];
    const facts: [string, string][] = [
      ["Fecha", quote.date ? formatDate(quote.date) : "Por definir"],
      ["Horario", clock(quote.time)],
      [quote.service === "school" ? "Alumnos" : "Personas", String(quote.people)],
      ...(quote.service === "school" && quote.school?.teachers ? [["Profesores", `${quote.school.teachers} · cortesía`] as [string, string]] : []),
      ...(quote.service === "school" && quote.school?.companions ? [["Acompañantes", String(quote.school.companions)] as [string, string]] : []),
      ["Válida hasta", formatDate(quote.expires_at)],
    ];
    const terms = quote.terms.trim() && quote.terms.trim() !== "Sin condiciones adicionales." ? quote.terms : "";
    return (
      <article ref={ref} className="quote-doc" aria-label="Vista previa de la cotización">
        <header className="quote-doc__head">
          <img src="/ruta-magica-logo.png" alt="" />
          <div>
            <strong>{(quote.service === "school" ? "Experiencias escolares" : serviceNames[quote.service] ?? "Experiencias").toUpperCase()}</strong>
            <span>TEOTIHUACÁN, MÉXICO</span>
          </div>
          <div className="quote-doc__folio">
            <strong>COTIZACIÓN</strong>
            <span>Folio {quote.id.slice(0, 8).toUpperCase()} · v{quote.version}</span>
            <span>Emitida el {formatDate(quote.updated_at)}</span>
          </div>
        </header>
        <p className="quote-doc__eyebrow">Propuesta para</p>
        <h2 className="quote-doc__title">{quote.customer.name}</h2>
        <p className="quote-doc__rule" aria-hidden="true"><i /><i /></p>
        {(quote.customer.phone || quote.customer.email) && <p className="quote-doc__contact">{[quote.customer.phone, quote.customer.email].filter(Boolean).join(" · ")}</p>}
        {quote.status === "draft" && <p className="quote-doc__stamp">Borrador · aún no enviado</p>}
        <dl className="quote-doc__facts">
          {facts.map(([label, value]) => (
            <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
          ))}
        </dl>
        {school.length > 0 && (
          <section className="quote-doc__section">
            <h3>Tu experiencia escolar</h3>
            {school.map(({ level, grades, workshops }) => (
              <div key={level.id} className="quote-doc__level">
                <p className="quote-doc__level-head"><strong>{level.name.toUpperCase()}</strong>{grades.length > 0 && <span>{listJoin(grades)} {level.gradeUnit}{grades.length > 1 ? "s" : ""}</span>}</p>
                <p className="quote-doc__muted">Incluye {level.includes}</p>
                {workshops.map((workshop) => (
                  <div key={workshop.id} className="quote-doc__workshop">
                    <img src={`/brochure-icons/${icons[workshop.id] ?? "hands"}.png`} alt="" />
                    <div><strong>{workshop.name}{workshop.grades ? ` · ${workshop.grades}` : ""}</strong><span>{workshop.description}</span></div>
                  </div>
                ))}
              </div>
            ))}
          </section>
        )}
        {quote.service === "school" && (
          <>
            <section className="quote-doc__section">
              <h3>Qué incluye</h3>
              <div className="quote-doc__includes">
                {SCHOOL_VISIT_INCLUDES.map((item) => (
                  <div key={item.title} className="quote-doc__workshop">
                    <img src={`/brochure-icons/${item.icon}.png`} alt="" />
                    <div><strong>{item.title}</strong><span>{item.text}</span></div>
                  </div>
                ))}
              </div>
            </section>
            <section className="quote-doc__section">
              <h3>Itinerario de referencia</h3>
              <dl className="quote-doc__itinerary">
                {SCHOOL_VISIT_ITINERARY.map(([hour, activity]) => (
                  <div key={hour}><dt>{hour}</dt><dd>{activity}</dd></div>
                ))}
              </dl>
              <p className="quote-doc__muted">El horario se ajusta con el grupo al confirmar la visita.</p>
            </section>
            <section className="quote-doc__section">
              <h3>{quote.school?.menu ? "Menú elegido" : "Menú a elegir"}</h3>
              <p className="quote-doc__muted">{quote.school?.menu ? "" : "Elige una opción para tu grupo. "}Todos los menús incluyen arroz, frijoles, salsa de xoconostle, agua de sabor y fruta de temporada.</p>
              <ul className="quote-doc__menus">
                {(quote.school?.menu ? [quote.school.menu] : schoolMenus(quote.school?.levels ?? [])).map((menu) => <li key={menu}>{menu}</li>)}
              </ul>
            </section>
          </>
        )}
        {showMoney && (
          <section className="quote-doc__section">
            <h3>Inversión</h3>
            <table className="quote-doc__items">
              <thead>
                <tr><th>Concepto</th><th>Cantidad</th><th>Precio</th><th>Importe</th></tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                    <td>{money(item.unit_price, quote.currency)}</td>
                    <td>{money(item.total ?? item.quantity * item.unit_price, quote.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="quote-doc__total">
              <div><span>Total</span><strong>{money(quote.total, quote.currency)}</strong><em>{quote.people} {quote.service === "school" ? "alumnos" : "personas"}</em></div>
              <img src="/brochure-icons/pyramid.png" alt="" />
            </div>
            {quote.service === "school" && (
              <ul className="quote-doc__notes">{SCHOOL_VISIT_NOTES.map((note) => <li key={note}>{note}</li>)}</ul>
            )}
          </section>
        )}
        {showMoney && (() => {
          const schedule = paymentSchedule(quote.payment_terms ?? { kind: "full_now", upfront: 0, balance_due: "service_day" }, quote.total, quote.date, quote.prepaid ?? 0);
          const when = schedule.dueDate ? `${schedule.dayBefore ? "A más tardar el" : "El"} ${formatDate(schedule.dueDate)}` : schedule.dayBefore ? "Un día antes del servicio" : "El día del servicio";
          return (
            <section className="quote-doc__section">
              <h3>Cómo se paga</h3>
              {schedule.prepaid > 0 && <p className="quote-doc__pay"><span><strong>Pago recibido</strong><small>Ya lo recibimos. ¡Gracias!</small></span><b>{money(schedule.prepaid, quote.currency)}</b></p>}
              {schedule.prepaid > 0 && schedule.rest <= 0 && <p className="quote-doc__muted">Servicio pagado por completo.</p>}
              {schedule.prepaid === 0 && schedule.kind === "reservation_fee" && <p className="quote-doc__pay"><span><strong>Apartado de fecha</strong><small>Se descuenta del total; no es reembolsable. Asegura tu fecha.</small></span><b>{money(schedule.upfront, quote.currency)}</b></p>}
              {schedule.prepaid === 0 && schedule.kind === "advance" && <p className="quote-doc__pay"><span><strong>Anticipo para reservar</strong><small>Se descuenta del total.</small></span><b>{money(schedule.upfront, quote.currency)}</b></p>}
              {schedule.prepaid === 0 && schedule.kind === "full_now" && <p className="quote-doc__pay"><span><strong>Pago al reservar</strong><small>Total del servicio.</small></span><b>{money(schedule.upfront, quote.currency)}</b></p>}
              {schedule.rest > 0 && <p className="quote-doc__pay quote-doc__pay--rest"><span><strong>{schedule.prepaid > 0 || schedule.kind !== "on_service_day" ? "Resto" : "Total del servicio"}</strong><small className={schedule.dayBefore ? "is-due" : ""}>{when}{schedule.dayBefore ? " (obligatorio)" : ""}. Efectivo o transferencia.</small></span><b>{money(schedule.rest, quote.currency)}</b></p>}
            </section>
          );
        })()}
        <section className="quote-doc__section">
          <h3>Forma de pago: transferencia</h3>
          <p className="quote-doc__muted">Los datos bancarios aparecen en el PDF. El cliente envía su comprobante por WhatsApp.</p>
        </section>
        {(terms || quote.service === "school") && (
          <section className="quote-doc__section">
            <h3>Condiciones</h3>
            {quote.service === "school" && SCHOOL_VISIT_CONDITIONS.map(([title, detail]) => (
              <p key={title} className="quote-doc__condition"><strong>{title}</strong><span>{detail}</span></p>
            ))}
            {terms && <p className="quote-doc__terms">{terms}</p>}
          </section>
        )}
        <section className="quote-doc__section">
          <h3>Organiza tu visita</h3>
          <dl className="quote-doc__contacts">
            {RUTA_MAGICA_CONTACTS.map((contact) => (
              <div key={contact.value}><dt><img src={`/brochure-icons/${contact.icon}.png`} alt="" />{contact.label}</dt><dd>{contact.value}</dd></div>
            ))}
          </dl>
        </section>
        <footer className="quote-doc__foot">TEOTIHUACÁN · EXPERIENCIAS RUTA MÁGICA</footer>
      </article>
    );
  },
);

/**
 * Prints only the quote page through a hidden frame, so the browser's "Guardar como PDF"
 * produces a clean file named after the customer.
 */
export function printQuote(element: HTMLElement, fileName: string) {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.append(frame);
  const doc = frame.contentDocument!;
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((node) => node.outerHTML)
    .join("");
  doc.open();
  doc.write(
    `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${fileName.replace(/[<>&"]/g, "")}</title><base href="${location.origin}/">${styles}<style>@page{size:letter;margin:14mm}html,body{background:#fff;margin:0}.quote-doc{box-shadow:none;border:0;margin:0;max-width:none}</style></head><body>${element.outerHTML}</body></html>`,
  );
  doc.close();
  const run = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), 1000);
  };
  const logo = doc.querySelector("img");
  if (logo && !logo.complete) logo.addEventListener("load", run, { once: true });
  else window.setTimeout(run, 150);
}
